import { ReactNode, createContext, useContext } from 'react';
import {
  TAvailableProducts,
  TGetIntroducerContextResponse,
  TGetIntroducerResponse,
  TSupportedIntroducerTypes,
} from 'lendingApi/lendingApi.types';
import {
  useQueryContext,
  useQueryIntroducer,
} from 'lendingApi/lendingApiHooks';
import { TIntroducerPermissions } from 'types';
import { getCookie } from 'utils/cookieHelper';

type TSplitTests = {
  hasAccessToPrimeLoans: boolean;
  allow24mFlexiLoanTerm: boolean;
  hasNewSignUpOnContext: boolean;
  hasMultipleOffersOnContext: boolean;
  commissionAwarePricingEnabled: boolean;
  introducerAccountPrimeCustomerLowPricing: boolean;
  accessToInterestOnlyLoans: boolean;
};

export type TIntroducerContext = {
  isLoggedIn: boolean;
  splitTests: TSplitTests | null;
  introducerId: string | null;
  introducer: TGetIntroducerResponse['data']['introducer'] | null;
  introducerCompany:
    | TGetIntroducerResponse['data']['introducer_company']
    | null;
  context: TGetIntroducerContextResponse['data'] | null;
  introducerType: TSupportedIntroducerTypes | null;
  availableProducts: TAvailableProducts[] | null;
  hasProductsForApplication: boolean;
  contextError: Error | null;
  isContextError: boolean;
  isIntroducerError: boolean;
  isContextLoading: boolean;
  isIntroducerLoading: boolean;
  introducerStatus: 'success' | 'error' | 'pending';
  checkIntroducerPermission: (permission: TIntroducerPermissions) => boolean;
};

const IntroducerContext = createContext<TIntroducerContext>({
  introducer: null,
  introducerCompany: null,
  context: null,
  isLoggedIn: false,
  splitTests: null,
  introducerType: null,
  availableProducts: null,
  hasProductsForApplication: false,
  contextError: null,
  isContextError: false,
  isIntroducerError: false,
  isContextLoading: true,
  isIntroducerLoading: true,
  introducerId: null,
  introducerStatus: 'pending',
  checkIntroducerPermission: () => false,
});

export const IntroducerContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const {
    data: context,
    isLoading: isContextLoading,
    isError: isContextError,
    error: contextError,
  } = useQueryContext();
  const {
    data,
    isLoading: isIntroducerLoading,
    isError: isIntroducerError,
    status: introducerStatus,
  } = useQueryIntroducer();
  const introducer = data?.data;
  const splitTests: TSplitTests | null = isContextError
    ? null
    : {
        hasNewSignUpOnContext:
          context?.data?.introducer_account_new_sign_up || false,
        hasMultipleOffersOnContext:
          context?.data?.introducer_portal_multiple_formal_confirmed_offers ||
          false,
        hasAccessToPrimeLoans: context?.data?.access_to_prime_loans || false,
        allow24mFlexiLoanTerm:
          context?.data?.allow_24m_flexi_loan_term || false,
        commissionAwarePricingEnabled:
          context?.data?.commission_aware_pricing_enabled || false,
        introducerAccountPrimeCustomerLowPricing:
          context?.data?.introducer_account_prime_customer_low_pricing || false,
        accessToInterestOnlyLoans:
          context?.data?.access_to_interest_only_loans || false,
      };

  const checkIntroducerPermission = (permission: TIntroducerPermissions) =>
    Boolean(introducer?.introducer[permission]);

  return (
    <IntroducerContext.Provider
      value={{
        isLoggedIn: getCookie('isLoggedIn') === 'True',
        splitTests,
        isContextLoading,
        isIntroducerLoading,
        contextError,
        isContextError,
        isIntroducerError,
        introducerStatus,
        introducerId: context?.data?.introducer_id || null,
        context: context?.data || null,
        introducer: introducer?.introducer || null,
        introducerCompany: introducer?.introducer_company || null,
        introducerType: introducer?.introducer_company.introducer_type || null,
        availableProducts:
          introducer?.introducer_company.available_products || null,
        hasProductsForApplication: (
          introducer?.introducer_company.available_products || []
        ).some(
          (product) => product === 'credit_facility' || product === 'flexi24',
        ),
        checkIntroducerPermission,
      }}
    >
      {children}
    </IntroducerContext.Provider>
  );
};

export const useIntroducerContext = (): TIntroducerContext => {
  const context = useContext(IntroducerContext);
  if (!context) {
    throw new Error(
      'useIntroducer must be used within a IntroducerContextProvider',
    );
  }
  return context;
};
